import React, { useEffect, useState } from "react";
import { useHistory, useLocation } from "react-router-dom";
import * as yup from "yup";
import "../../assetss/css/EditarPaciente.css";
import { ModeloPost, ModeloUpdate } from "../ModeloCrud";
import TextField from "@mui/material/TextField";
import Switch from "@mui/material/Switch";
import crypto from "../Crypto";
import Typography from "@mui/material/Typography";
import { useFormik } from "formik";
import Grid from "@mui/material/Grid";
import Box from "@mui/material/Box";
import Paper from "@mui/material/Paper";
import Container from "@mui/material/Container";
import FormControlLabel from "@mui/material/FormControlLabel";
import Divider from "@mui/material/Divider";
import BackPage from "../BackPage";
import SaveCancelButtons from "../SaveCancelButtons";
import FacebookCircularProgress from "../FacebookCircularProgress";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import ProtectedRoute from "../ProtectedRoute";
import { toast } from "react-toastify";

// ---------------------------------------------------------------------------
// Config de campos booleanos: un solo array en vez de repetir 8 switches
// (regla de 3: acá ya son 8 campos iguales, así que vale la pena la lista)
// ---------------------------------------------------------------------------
const BOOLEAN_FIELDS = [
  { name: "requiere_pieza", label: "Requiere pieza" },
  { name: "requiere_cara", label: "Requiere cara" },
  { name: "requiere_sector", label: "Requiere sector" },
  { name: "requiere_pilar", label: "Requiere pilar" },
  { name: "multiples_piezas", label: "Múltiples piezas" },
  { name: "paso_intermedio", label: "Paso intermedio" },
  { name: "genera_odontograma", label: "Genera odontograma" },
  { name: "figura_odontograma", label: "Figura odontograma" },
];

// Máscara 99:99:99 (6 dígitos + 2 ":" fijos en las posiciones 3 y 6,
// total 8 caracteres, coincide con el max:8 de la regla del modelo).
// El ":" se inserta apenas se completa cada par de dígitos, no recién
// al empezar el siguiente par. Sin libs externas.
function digitsFromCodigoMask(maskedValue) {
  return maskedValue.replace(/\D/g, "").slice(0, 6);
}

function codigoMaskFromDigits(digits) {
  const p1 = digits.slice(0, 2);
  const p2 = digits.slice(2, 4);
  const p3 = digits.slice(4, 6);
  let out = p1;
  if (digits.length >= 2) out += ":";
  out += p2;
  if (digits.length >= 4) out += ":";
  out += p3;
  return out;
}

const initialValues = {
  codigo: "",
  descripcion: "",
  edad_desde: "",
  edad_hasta: "",
  nivel_auditoria: "",
  duracion_estimada: "",
  item_bas: "",
  tasaiva: "",
  imagen_odontograma_anterior: "",
  imagen_odontograma_arealizar: "",
  referencias_fichados_a_id: "",
  referencias_fichados_r_id: "",
  habilitado: true,
  requiere_pieza: false,
  requiere_cara: false,
  requiere_sector: false,
  requiere_pilar: false,
  multiples_piezas: false,
  paso_intermedio: false,
  genera_odontograma: false,
  figura_odontograma: false,
};

const validationSchema = yup.object({
  codigo: yup.string().required("Campo obligatorio.").max(8, "Máximo 8 caracteres."),
  descripcion: yup
    .string()
    .required("Campo obligatorio.")
    .max(50, "Máximo 50 caracteres."),
  edad_desde: yup
    .number()
    .transform((v, orig) => (orig === "" ? null : v))
    .nullable()
    .integer("Debe ser un número entero.")
    .min(0, "No puede ser negativo."),
  edad_hasta: yup
    .number()
    .transform((v, orig) => (orig === "" ? null : v))
    .nullable()
    .integer("Debe ser un número entero.")
    .min(yup.ref("edad_desde"), "No puede ser menor a edad desde.")
    .typeError("Debe ser un número entero."),
  nivel_auditoria: yup
    .string()
    .nullable()
    .max(1, "Un solo carácter."),
  duracion_estimada: yup
    .number()
    .transform((v, orig) => (orig === "" ? null : v))
    .nullable()
    .integer("Debe ser un número entero.")
    .min(0, "No puede ser negativo."),
  item_bas: yup.string().nullable().max(15, "Máximo 15 caracteres."),
  tasaiva: yup
    .number()
    .transform((v, orig) => (orig === "" ? null : v))
    .nullable()
    .min(0, "No puede ser negativo."),
  imagen_odontograma_anterior: yup
    .string()
    .nullable()
    .max(50, "Máximo 50 caracteres."),
  imagen_odontograma_arealizar: yup
    .string()
    .nullable()
    .max(50, "Máximo 50 caracteres."),
  // referencias_fichados_a_id / referencias_fichados_r_id: a definir cómo se cargan
  // (por ahora sólo se valida que si tienen valor sea numérico entero)
  referencias_fichados_a_id: yup
    .number()
    .transform((v, orig) => (orig === "" ? null : v))
    .nullable()
    .integer("Debe ser un número entero."),
  referencias_fichados_r_id: yup
    .number()
    .transform((v, orig) => (orig === "" ? null : v))
    .nullable()
    .integer("Debe ser un número entero."),
});

const InsertUpdateNomenclador = () => {
  const location = useLocation();
  const history = useHistory();

  const [loading, setLoading] = useState(false);
  const [isAlta, setIsAlta] = useState(true);
  const [nomencladorId, setNomencladorId] = useState(null);

  const userRoles =
    crypto.decryptLocalStorage("user_roles") !== null
      ? crypto.decryptLocalStorage("user_roles")
      : [];

  const iconStyles = {
    color: "avatar",
    fontSize: 32,
    marginLeft: 1,
    marginRight: 1,
  };

  const formik = useFormik({
    initialValues,
    validationSchema,
    onSubmit: (values) => {
      grabarNomenclador(values);
    },
  });

  useEffect(() => {
    if (typeof location.currentNomenclador === "undefined") {
      setIsAlta(true);
    } else {
      setIsAlta(false);
      setNomencladorId(location.currentNomenclador.id);
      fillInitialValues(location.currentNomenclador);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function fillInitialValues(nomenclador) {
    formik.resetForm();
    Object.keys(initialValues).forEach((key) => {
      if (nomenclador[key] !== undefined && nomenclador[key] !== null) {
        formik.setFieldValue(key, nomenclador[key]);
      }
    });
  }

  function handleClickVolver() {
    history.push("/nomencladores");
  }

  async function grabarNomenclador(values) {
    const wasAlta = isAlta;
    setLoading(true);

    const Data = {
      codigo: values.codigo,
      descripcion: values.descripcion,
      edad_desde: values.edad_desde === "" ? null : values.edad_desde,
      edad_hasta: values.edad_hasta === "" ? null : values.edad_hasta,
      nivel_auditoria: values.nivel_auditoria || null,
      duracion_estimada:
        values.duracion_estimada === "" ? null : values.duracion_estimada,
      item_bas: values.item_bas,
      tasaiva: values.tasaiva === "" ? null : values.tasaiva,
      imagen_odontograma_anterior: values.imagen_odontograma_anterior,
      imagen_odontograma_arealizar: values.imagen_odontograma_arealizar,
      referencias_fichados_a_id:
        values.referencias_fichados_a_id === ""
          ? null
          : values.referencias_fichados_a_id,
      referencias_fichados_r_id:
        values.referencias_fichados_r_id === ""
          ? null
          : values.referencias_fichados_r_id,
      habilitado: values.habilitado ? 1 : 0,
      requiere_pieza: values.requiere_pieza ? 1 : 0,
      requiere_cara: values.requiere_cara ? 1 : 0,
      requiere_sector: values.requiere_sector ? 1 : 0,
      requiere_pilar: values.requiere_pilar ? 1 : 0,
      multiples_piezas: values.multiples_piezas ? 1 : 0,
      paso_intermedio: values.paso_intermedio ? 1 : 0,
      genera_odontograma: values.genera_odontograma ? 1 : 0,
      figura_odontograma: values.figura_odontograma ? 1 : 0,
    };

    let result = null;
    if (wasAlta) {
      result = await ModeloPost("nomenclador", null, null, Data);
    } else {
      result = await ModeloUpdate("nomenclador", nomencladorId, Data);
    }
    setLoading(false);

    if (result.error === false) {
      const nomencladorGuardado = result.listado[0];
      crypto.encryptDataStorage("currentNomenclador", nomencladorGuardado);

      // Seguimos parados en el mismo registro, ya con los datos que
      // devolvió el backend (y en modo edición si era un alta).
      setIsAlta(false);
      setNomencladorId(nomencladorGuardado.id);
      fillInitialValues(nomencladorGuardado);

      toast.success(
        wasAlta
          ? "Nomenclador creado correctamente."
          : "Nomenclador actualizado correctamente."
      );
    } else {
      const mensaje =
        (result.errormessage &&
          result.errormessage[0] &&
          result.errormessage[0]["error"]) ||
        result.mensaje ||
        "Ocurrió un error al grabar.";
      toast.error(mensaje);
    }
  }

  return (
    <ProtectedRoute userRoles={userRoles} requiredPermission={"ALTA NOMENCLADOR"}>
      <BackPage text="Volver" handleClick={handleClickVolver} />
      <Container maxWidth="xl">
        <Grid xs={12} sm={6}>
          <Paper elevation={2} sx={{ width: "50%", height: "55px", marginBottom: 4 }}>
            <Box
              sx={{
                display: "flex",
                justifyContent: "start",
                alignItems: "center",
                height: "100%",
              }}
            >
              {!isAlta ? <EditIcon sx={iconStyles} /> : <AddIcon sx={iconStyles} />}
              <Typography variant="h6">
                {!isAlta ? "Editar nomenclador" : "Nuevo nomenclador"}
              </Typography>
            </Box>
          </Paper>
        </Grid>
      </Container>

      {loading && (
        <div className="flex justify-center mt-2">
          <FacebookCircularProgress />
        </div>
      )}

      {!loading && (
        <form key="nomenclador-form" onSubmit={formik.handleSubmit}>
          <Container
            maxWidth="xl"
            sx={{ borderBottom: 1, borderColor: "grey.400", paddingBottom: 2 }}
          >
            {/* --- Identificación --- */}
            <Typography variant="subtitle2" sx={{ marginTop: 2, color: "text.secondary" }}>
              Identificación
            </Typography>
            <Grid container spacing={1} sx={{ marginBottom: 2 }}>
              <Grid xs={12} md={2}>
                <TextField
                  id="codigo"
                  label="Código"
                  placeholder="99:99:99"
                  value={formik.values.codigo}
                  onChange={(e) =>
                    formik.setFieldValue(
                      "codigo",
                      codigoMaskFromDigits(digitsFromCodigoMask(e.target.value))
                    )
                  }
                  onKeyDown={(e) => {
                    if (e.key === "Backspace" && formik.values.codigo.endsWith(":")) {
                      e.preventDefault();
                      const digits = digitsFromCodigoMask(formik.values.codigo).slice(
                        0,
                        -1
                      );
                      formik.setFieldValue("codigo", codigoMaskFromDigits(digits));
                    }
                  }}
                  onBlur={formik.handleBlur}
                  helperText={formik.touched.codigo ? formik.errors.codigo : ""}
                  error={formik.touched.codigo && Boolean(formik.errors.codigo)}
                  inputProps={{ maxLength: 8, inputMode: "numeric" }}
                  margin="dense"
                  variant="outlined"
                  size="small"
                  fullWidth
                  sx={{ marginTop: 2 }}
                />
              </Grid>
              <Grid xs={12} md={6}>
                <TextField
                  id="descripcion"
                  label="Descripción"
                  value={formik.values.descripcion}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  helperText={formik.touched.descripcion ? formik.errors.descripcion : ""}
                  error={formik.touched.descripcion && Boolean(formik.errors.descripcion)}
                  margin="dense"
                  variant="outlined"
                  size="small"
                  fullWidth
                  sx={{ marginTop: 2, marginLeft: { xs: 0, md: 2 } }}
                />
              </Grid>
              <Grid xs={12} md={2}>
                <TextField
                  id="item_bas"
                  label="Item BAS"
                  value={formik.values.item_bas}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  helperText={formik.touched.item_bas ? formik.errors.item_bas : ""}
                  error={formik.touched.item_bas && Boolean(formik.errors.item_bas)}
                  margin="dense"
                  variant="outlined"
                  size="small"
                  fullWidth
                  sx={{ marginTop: 2, marginLeft: { xs: 0, md: 2 } }}
                />
              </Grid>
              <Grid xs={12} md={2}>
                <TextField
                  id="nivel_auditoria"
                  label="Nivel auditoría"
                  value={formik.values.nivel_auditoria}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  helperText={
                    formik.touched.nivel_auditoria ? formik.errors.nivel_auditoria : ""
                  }
                  error={
                    formik.touched.nivel_auditoria &&
                    Boolean(formik.errors.nivel_auditoria)
                  }
                  inputProps={{ maxLength: 1 }}
                  margin="dense"
                  variant="outlined"
                  size="small"
                  fullWidth
                  sx={{ marginTop: 2, marginLeft: { xs: 0, md: 2 } }}
                />
              </Grid>
            </Grid>

            {/* --- Edades y duración --- */}
            <Typography variant="subtitle2" sx={{ color: "text.secondary" }}>
              Edades y duración
            </Typography>
            <Grid container spacing={1} sx={{ marginBottom: 2 }}>
              <Grid xs={12} md={2}>
                <TextField
                  id="edad_desde"
                  label="Edad desde"
                  type="number"
                  value={formik.values.edad_desde}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  helperText={formik.touched.edad_desde ? formik.errors.edad_desde : ""}
                  error={formik.touched.edad_desde && Boolean(formik.errors.edad_desde)}
                  margin="dense"
                  variant="outlined"
                  size="small"
                  fullWidth
                  sx={{ marginTop: 2 }}
                />
              </Grid>
              <Grid xs={12} md={2}>
                <TextField
                  id="edad_hasta"
                  label="Edad hasta"
                  type="number"
                  value={formik.values.edad_hasta}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  helperText={formik.touched.edad_hasta ? formik.errors.edad_hasta : ""}
                  error={formik.touched.edad_hasta && Boolean(formik.errors.edad_hasta)}
                  margin="dense"
                  variant="outlined"
                  size="small"
                  fullWidth
                  sx={{ marginTop: 2, marginLeft: { xs: 0, md: 2 } }}
                />
              </Grid>
              <Grid xs={12} md={2}>
                <TextField
                  id="duracion_estimada"
                  label="Duración estimada (min)"
                  type="number"
                  value={formik.values.duracion_estimada}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  helperText={
                    formik.touched.duracion_estimada
                      ? formik.errors.duracion_estimada
                      : ""
                  }
                  error={
                    formik.touched.duracion_estimada &&
                    Boolean(formik.errors.duracion_estimada)
                  }
                  margin="dense"
                  variant="outlined"
                  size="small"
                  fullWidth
                  sx={{ marginTop: 2, marginLeft: { xs: 0, md: 2 } }}
                />
              </Grid>
            </Grid>

            {/* --- Reglas clínicas (booleanos) --- */}
            <Typography variant="subtitle2" sx={{ color: "text.secondary" }}>
              Reglas clínicas
            </Typography>
            <Grid container spacing={1} sx={{ marginBottom: 2 }}>
              {BOOLEAN_FIELDS.map((field) => (
                <Grid xs={6} md={3} key={field.name}>
                  <FormControlLabel
                    control={
                      <Switch
                        id={field.name}
                        checked={Boolean(formik.values[field.name])}
                        onChange={(e) =>
                          formik.setFieldValue(field.name, e.target.checked)
                        }
                      />
                    }
                    label={
                      <Typography
                        sx={{ fontSize: 14 }}
                        color={formik.values[field.name] ? "avatar" : "#94a3b8"}
                        fontWeight={formik.values[field.name] ? "bold" : "regular"}
                      >
                        {field.label}
                      </Typography>
                    }
                    sx={{ marginTop: 1 }}
                  />
                </Grid>
              ))}
            </Grid>

            {/* --- Odontograma --- */}
            <Typography variant="subtitle2" sx={{ color: "text.secondary" }}>
              Odontograma
            </Typography>
            <Grid container spacing={1} sx={{ marginBottom: 2 }}>
              <Grid xs={12} md={4}>
                <TextField
                  id="imagen_odontograma_anterior"
                  label="Imagen odontograma anterior"
                  value={formik.values.imagen_odontograma_anterior}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  helperText={
                    formik.touched.imagen_odontograma_anterior
                      ? formik.errors.imagen_odontograma_anterior
                      : ""
                  }
                  error={
                    formik.touched.imagen_odontograma_anterior &&
                    Boolean(formik.errors.imagen_odontograma_anterior)
                  }
                  margin="dense"
                  variant="outlined"
                  size="small"
                  fullWidth
                  sx={{ marginTop: 2 }}
                />
              </Grid>
              <Grid xs={12} md={4}>
                <TextField
                  id="imagen_odontograma_arealizar"
                  label="Imagen odontograma a realizar"
                  value={formik.values.imagen_odontograma_arealizar}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  helperText={
                    formik.touched.imagen_odontograma_arealizar
                      ? formik.errors.imagen_odontograma_arealizar
                      : ""
                  }
                  error={
                    formik.touched.imagen_odontograma_arealizar &&
                    Boolean(formik.errors.imagen_odontograma_arealizar)
                  }
                  margin="dense"
                  variant="outlined"
                  size="small"
                  fullWidth
                  sx={{ marginTop: 2, marginLeft: { xs: 0, md: 2 } }}
                />
              </Grid>
            </Grid>

            {/* --- Facturación --- */}
            <Typography variant="subtitle2" sx={{ color: "text.secondary" }}>
              Facturación
            </Typography>
            <Grid container spacing={1} sx={{ marginBottom: 2 }}>
              <Grid xs={12} md={2}>
                <TextField
                  id="tasaiva"
                  label="Tasa IVA"
                  type="number"
                  value={formik.values.tasaiva}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  helperText={formik.touched.tasaiva ? formik.errors.tasaiva : ""}
                  error={formik.touched.tasaiva && Boolean(formik.errors.tasaiva)}
                  margin="dense"
                  variant="outlined"
                  size="small"
                  fullWidth
                  sx={{ marginTop: 2 }}
                />
              </Grid>
              <Grid xs={12} md={3}>
                <FormControlLabel
                  control={
                    <Switch
                      id="habilitado"
                      checked={Boolean(formik.values.habilitado)}
                      onChange={(e) =>
                        formik.setFieldValue("habilitado", e.target.checked)
                      }
                    />
                  }
                  label={
                    <Typography
                      color={formik.values.habilitado ? "avatar" : "#94a3b8"}
                      fontWeight={formik.values.habilitado ? "bold" : "regular"}
                    >
                      Habilitado
                    </Typography>
                  }
                  sx={{ marginTop: 2, marginLeft: { xs: 0, md: 2 } }}
                />
              </Grid>
            </Grid>

            {/* --- Referencias fichados: a definir cómo se cargan --- */}
            <Divider sx={{ marginY: 1 }} />
            <Typography variant="subtitle2" sx={{ color: "text.secondary" }}>
              Referencias fichados (provisorio — a resolver cómo se cargan)
            </Typography>
            <Grid container spacing={1} sx={{ marginBottom: 2 }}>
              <Grid xs={12} md={2}>
                <TextField
                  id="referencias_fichados_a_id"
                  label="Ref. fichado A (id)"
                  type="number"
                  value={formik.values.referencias_fichados_a_id}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  helperText={
                    formik.touched.referencias_fichados_a_id
                      ? formik.errors.referencias_fichados_a_id
                      : ""
                  }
                  error={
                    formik.touched.referencias_fichados_a_id &&
                    Boolean(formik.errors.referencias_fichados_a_id)
                  }
                  margin="dense"
                  variant="outlined"
                  size="small"
                  fullWidth
                  sx={{ marginTop: 2 }}
                />
              </Grid>
              <Grid xs={12} md={2}>
                <TextField
                  id="referencias_fichados_r_id"
                  label="Ref. fichado R (id)"
                  type="number"
                  value={formik.values.referencias_fichados_r_id}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  helperText={
                    formik.touched.referencias_fichados_r_id
                      ? formik.errors.referencias_fichados_r_id
                      : ""
                  }
                  error={
                    formik.touched.referencias_fichados_r_id &&
                    Boolean(formik.errors.referencias_fichados_r_id)
                  }
                  margin="dense"
                  variant="outlined"
                  size="small"
                  fullWidth
                  sx={{ marginTop: 2, marginLeft: { xs: 0, md: 2 } }}
                />
              </Grid>
            </Grid>
          </Container>

          <SaveCancelButtons handleCancel={handleClickVolver} loading={loading} />
        </form>
      )}

      <BackPage text="Volver" handleClick={handleClickVolver} />
    </ProtectedRoute>
  );
};

export default InsertUpdateNomenclador;
